import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Instagram, Camera, Star } from 'lucide-react';
import galleryStatic from '../data/galleryStatic';
import { useScrollReveal } from '../hooks/useScrollReveal';

const GalleryPage = () => {
  const galleryImages = galleryStatic;
  const headerReveal = useScrollReveal({ threshold: 0.1 });

  return (
    <div className="min-h-screen visitor-page">
      <Navbar />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-white via-domigo-pink/10 to-domigo-purple/5 py-16 lg:py-20">
        <div
          ref={headerReveal.ref}
          className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center transition-all duration-1000 ${headerReveal.isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-12'
            }`}
        >
          <div className="inline-flex items-center justify-center mb-6">
            <div className="p-4 bg-gradient-pink rounded-3xl shadow-pink">
              <Camera className="text-white" size={48} />
            </div>
          </div>
          <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-gray-900 via-domigo-pink to-domigo-purple bg-clip-text text-transparent leading-tight mb-6">
            Our Happy Pets Gallery
          </h1>
          <p className="text-lg lg:text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Take a peek at the wonderful moments we share with our furry friends. From grooming
            transformations to playful daycare sessions, every day is filled with joy!
          </p>

          {/* Instagram Link */}
          <a
            href="https://www.instagram.com/domigoindia?igsh=MWswcnE0bTk1dWw3&utm_source=qr"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 bg-gradient-pink text-white px-8 py-4 rounded-full text-lg font-bold hover:shadow-pink transition-all duration-300 transform hover:scale-105"
          >
            <Instagram size={24} />
            Follow @domigo on Instagram
          </a>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="bg-gradient-to-b from-white to-gray-50 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {galleryImages.length === 0 ? (
            <div className="text-center py-16">
              <Camera className="mx-auto text-gray-300 mb-4" size={64} />
              <p className="text-xl text-gray-500">No images available</p>
              <p className="text-gray-400 mt-2">Gallery images will appear here soon.</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
              {galleryImages.map((image, index) => (
                <div
                  key={image.src || index}
                  className="relative group aspect-square overflow-hidden rounded-2xl bg-gray-100 shadow-sm hover:shadow-lg transition-all duration-500 animate-fade-in cursor-pointer"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <img
                    src={image.src}
                    alt={image.alt || `Gallery image ${index + 1}`}
                    className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                    loading="lazy"
                  />
                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end p-4">
                    <div className="text-white transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300 flex items-center gap-2">
                      <Camera size={18} className="text-white" />
                      <span className="text-sm font-medium">View Moment</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            <div className="group">
              <div className="text-5xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                400+
              </div>
              <p className="text-gray-600 font-medium">Happy Pets</p>
            </div>
            <div className="group">
              <div className="text-5xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                4k+
              </div>
              <p className="text-gray-600 font-medium">Instagram Followers</p>
            </div>
            <div className="group">
              <div className="text-5xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                400+
              </div>
              <p className="text-gray-600 font-medium">Photo Moments</p>
            </div>
            <div className="group">
              <div className="text-5xl font-bold bg-gradient-to-r from-domigo-purple to-domigo-purple bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300">
                4.5
              </div>
              <p className="text-gray-600 font-medium flex items-center justify-center gap-1">
                Rating <Star size={20} className="text-yellow-400 fill-yellow-400" />
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-purple py-16 lg:py-24 relative overflow-hidden">
        {/* Decorative Elements */}
        <div className="absolute top-0 left-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/10 rounded-full blur-3xl"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h2 className="text-3xl lg:text-5xl font-bold text-white mb-6">
            Want Your Pet Featured Here?
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            Book a service with us and your furry friend might be our next Instagram star!
            Don't forget to tag us @domigo in your pet photos.
          </p>
          <a
            href="/contact"
            className="inline-block bg-white text-domigo-purple px-8 py-4 rounded-full text-lg font-bold hover:shadow-large transition-all duration-300 transform hover:scale-105"
          >
            Contact Us
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GalleryPage;
